<template>
    <div class="invoice-draft-list" tabindex="0">
        <ag-grid-vue
            class="w-full h-full min-w-[830px] ag-theme-balham overflow-auto"
            :class="{ 'h-full': 11 > 10, 'h-1/3': 11 <= 10 }"
            :row-data="rowData"
            :column-defs="columns"
            :grid-options="gridOptions"
            :theme="agTheme"
        />
    </div>
</template>

<script setup lang="ts">

import {agTheme} from '@/agUtils/ag-theme.service'
import {AgGridVue} from 'ag-grid-vue3'
import {ColDef, GridApi, GridOptions, GridReadyEvent, ICellRendererParams, ValueGetterParams, ValueSetterParams} from 'ag-grid-community'
import {globalDefaultColDef} from '@/agUtils'
import {computed, inject, nextTick, onMounted, onUnmounted, ref, Ref, shallowRef, toRaw} from 'vue'
import {InvoiceBalance} from '@/invoice/InvoiceBalance'
import {formatCurrency, formattedDate} from '@/services/formatter.service'
import InvoiceSaccCellRenderer from '@/invoice/InvoiceSaccCellRenderer.vue'
import {useInvoiceDrafts} from '@/invoice/InvoiceCreator/invoice.draft.service'
import {useInvoiceCreator} from '@/invoice/invoice.service'
import {createProductLine, ProductLine} from '@/invoice/InvoiceCreator/invoiceProductLine'
import InvoiceDraftActions from '@/invoice/InvoiceCreator/InvoiceDraftActions.vue'
import emitter from '@/services/infrastructure/emitter'
import {FOCUS_INVOICE_REF_FIELD, INVOICE_DRAFT_DELETED, INVOICE_DRAFT_SELECTED, INVOICE_DRAFT_UPDATED} from '@/types/invoice/InvoiceEvent'
import DraftNameCell from '@/draft/DraftNameCell.vue'
import {notifySuccess, notifyError} from '@/services/infrastructure/notification.service'

const panelId: Ref<number> | undefined = inject('panelId')
const panelPathSegment: Ref<number> | undefined = inject('panelPathSegment')

const gridApi = shallowRef<GridApi>()

const {invoiceData} = useInvoiceCreator()
const {drafts, deleteDraft, updateDraft, invoiceDraftUuid} = useInvoiceDrafts()

const rowData = computed(() => {
    return drafts.value.map((draft) => {
        return {
            content: draft.content,
            uuid: draft.uuid,
            name: draft.name,
            subAccountUuid: draft.content.selectedPickedOption?.subAccountUuid ?? ''
        }
    })
})


const selectedInvoiceDraft = ref<InvoiceBalance>()

const handleInvoiceDraftSelected = (invoiceDraft: any) => {
    selectedInvoiceDraft.value = invoiceDraft
    assignInvoiceData(invoiceData.value, toRaw(invoiceDraft.content))
    emitter.emit(INVOICE_DRAFT_SELECTED, invoiceDraft?.uuid)
    notifySuccess({ title: 'Suksess', description: `Kladden ${invoiceDraft.name} ble hentet.`})
}

const columns: ColDef[] = [
    {
        headerName: 'Kladd',
        field: 'name',
        editable: true,
        flex: 2,
        minWidth: 130,
        sort: 'asc',
        valueSetter: (params: ValueSetterParams) => {
            updateDraft({...params.data, name: params.newValue}).then(res => {
                if (res.isRight()) {
                    emitter.emit(INVOICE_DRAFT_UPDATED)
                    return true
                } else {
                    notifyError({ title: 'Feilet', description: `Kladden ${params.data.name} kunne ikke oppdateres.` })
                }
            })
            return false
        },
        cellClass: 'cursor-pointer',
        cellRenderer: DraftNameCell,
        cellRendererParams: (params: ICellRendererParams) => {
            const draft = params.data
            return {
                selectDraft: () => {
                    if (draft.uuid === invoiceDraftUuid.value) return
                    nextTick(
                        () => {
                            handleInvoiceDraftSelected(draft)
                        }
                    )
                },
            }
        },
        sortable: true
    },
    {
        headerName: 'Dato',
        field: 'content.invoiceDate',
        flex: 1,
        maxWidth: 120,
        cellRenderer: (params: ICellRendererParams) => {
            return formattedDate(params.value)
        },
        sortable: true
    },
    {
        headerName: 'Forfallsdato',
        field: 'content.dueDate',
        flex: 1,
        maxWidth: 120,
        cellRenderer: (params: ICellRendererParams) => {
            return formattedDate(params.value)
        },
        sortable: true
    },
    {
        headerName: 'Kunde',
        cellRenderer: InvoiceSaccCellRenderer,
        flex: 1,
        sortable: true
    },
    {
        headerName: 'Beløp',
        valueGetter: (params: ValueGetterParams) => {
            return params.data.content.amount
        },
        flex: 1,
        maxWidth: 120,
        cellRenderer: (node: ICellRendererParams) => formatCurrency(node.value, 2),
        sortable: true
    },
    {
        headerName: '',
        pinned: 'right',
        cellClass: 'text-right',
        flex: 1,
        maxWidth: 90,
        cellRenderer: InvoiceDraftActions,
        cellRendererParams: (params: ICellRendererParams) => {
            const draft = params.data
            return {
                deleteSingleDraft: async () => {
                    await deleteDraft(draft.uuid)
                    notifySuccess({ title: 'Slettet', description: `Kladden ${draft.name} ble slettet.` })
                    emitter.emit(INVOICE_DRAFT_DELETED)
                },
                selectDraft: () => {
                    if (draft.uuid === invoiceDraftUuid.value) return

                    nextTick(
                        () => {
                            handleInvoiceDraftSelected(draft)
                            notifySuccess({ title: 'Suksess', description: `Kladden ${draft.name} ble hentet.` })
                            emitter.emit(INVOICE_DRAFT_SELECTED, draft.uuid)
                        }
                    )
                },
            }
        },
        sortable: true
    },
]

const gridOptions: GridOptions = {
    rowHeight: 45,
    defaultColDef: {
        ...globalDefaultColDef,
        headerName: '',
        resizable: true,
        sortable: false,
        filter: false
    },
    onGridReady: (event: GridReadyEvent) => {
        gridApi.value = event.api
    },
    getRowId: (node: { data: { uuid: string } }) => {
        return node.data.uuid
    },
    context: {
        panelId,
        panelPathSegment,
        invoiceDraftUuid
    }
}

const assignInvoiceData = (target: any, source: any) => {
    for (const key in source) {
        if (['invoiceDate', 'dueDate', 'deliveryDate'].includes(key) && typeof source[key] === 'string') {
            target[key] = new Date(source[key])
        } else if (['productLines'].includes(key)) {
            target.productLines = source[key].map((line: ProductLine) => {
                const pl = createProductLine()
                Object.assign(pl, line)
                return pl
            })
        } else {
            target[key] = source[key]
        }
    }
}

const fetchDisplayedAgGridRows = () => {
    if (!gridApi.value) return
    const count = gridApi.value.getDisplayedRowCount()
    if (count === 0) return
    const rows: any[] = []
    for (let i = 0; i < count; i++) {
        const rowNode = gridApi.value.getDisplayedRowAtIndex(i)
        if (rowNode) rows.push(rowNode.data)
    }
    return rows
}

const traverseDraftList = (moveUp: boolean) => {
    const displayedRows = fetchDisplayedAgGridRows()
    if (!displayedRows) return

    if (!selectedInvoiceDraft.value) {
        const firstDraft = drafts.value.find(d => d.uuid === displayedRows[0].uuid)
        handleInvoiceDraftSelected(firstDraft)
        return
    }

    const currentIndex = displayedRows.findIndex(row => row.uuid === selectedInvoiceDraft.value?.uuid)
    if (currentIndex === -1) return

    const newIndex = moveUp ? currentIndex - 1 : currentIndex + 1
    if (newIndex < 0 || newIndex >= displayedRows.length) return

    const nextDraft = drafts.value.find(d => d.uuid === displayedRows[newIndex].uuid)
    handleInvoiceDraftSelected(nextDraft)
}

const handleArrowKeyNavigation = (event: KeyboardEvent) => {
    if (event.key === 'ArrowUp') {
        event.preventDefault()
        traverseDraftList(true)
    } else if (event.key === 'ArrowDown') {
        event.preventDefault()
        traverseDraftList(false)
    } else if (event.key === 'Tab') {
        event.preventDefault()
        emitter.emit(FOCUS_INVOICE_REF_FIELD)
    }
}

onMounted(() => {
    const gridDiv = document.querySelector('.invoice-draft-list')
    if (gridDiv) {
        gridDiv.addEventListener('keydown', handleArrowKeyNavigation as EventListener)
    }
})

onUnmounted(() => {
    const gridDiv = document.querySelector('.invoice-draft-list')
    if (gridDiv) {
        gridDiv.removeEventListener('keydown', handleArrowKeyNavigation as EventListener)
    }
})

</script>

<style scoped>

</style>